import {whitelabelService} from '@/services';
import exampleCSS from '@/data/examples/whitelabel.css'
import exampleEmailHTML from '@/data/examples/whitelabel.email.html'
import defaultFontsCSS from '@/styles/fonts.scss'
import defaultPrimaryFont from '@/data/defaults/primary.font'
import defaultSecondaryFont from '@/data/defaults/secondary.font'
import { setFontsCss } from '@/helpers/whitelabel'
import i18n from '@/plugins/i18n';
import WhitelabelFileTypes from "@/enums/WhitelabelFileTypes";
import { getSubdomain } from '@/helpers/subdomain'
import { safeDispatch } from '@/helpers/safeDispatch'

let initialState = {
  isValid: false,
  isLoading: false,
  activeWhitelabelTab: {
    "id": "custom-urls",
    "icon": "mdi-link-variant",
    "labelKey": "navigation.urls",
    "component": "CustomUrlsTab"
  },
  urls: {
    subdomain: '',
    faqUrl: '',
    footerAboutUrl: '',
    footerContactUrl: '',
  },
  email: {
    example: exampleEmailHTML,
    default: null,
    custom: ''
  },
  css: {
    example: exampleCSS,
    default: null,
    custom: ''
  },
  favicon: {
    default: null,
    custom: null
  },
  logos: {
    light: null,
    dark: null
  },
  fonts: {
    css: {
      example: '',
      default: defaultFontsCSS,
      custom: ''
    },
    primary: {
      name: '',
      weight: '',
      style: '',
      files: []
    },
    secondary: {
      name: '',
      weight: '',
      style: '',
      files: []
    }
  },
};

export default {
  state: () => initialState,
  mutations: {
    SAVE_WHITELABEL(state) {
      return state.activeWhitelabelTab
    },
    SET_ACTIVE_WHITELABEL_TAB(state, tab) {
      state.activeWhitelabelTab = tab;
    },
    SET_URLS(state, urls) {
      return state.urls = urls;
    },
    SET_EMAIL_HTML(state, emailHtml) {
      state.email.custom = emailHtml;
    },
    SET_LOGO_DARK(state, logo) {
      return state.logos.dark = logo;
    },
    SET_LOGO_LIGHT(state, logo) {
      return state.logos.light = logo;
    },
    SET_FAVICON(state, favicon) {
      return state.favicon.custom = favicon;
    },
    SET_FONTS(state, fonts) {
      state.fonts.primary = fonts[0];
      state.fonts.secondary = fonts[1];
    },
    SET_FONTS_CSS(state, fontCSS) {
      setFontsCss(fontCSS);
      return state.fonts.css.custom = fontCSS;
    },
    SET_CSS(state, customCSS) {
      return state.css.custom = customCSS;
    },
    RESET_WHITELABEL(state) {
      const s = initialState
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    }
  },
  actions: {
    setActiveWhitelabelTab({commit}, tab) {
      commit('SET_ACTIVE_WHITELABEL_TAB', tab);
    },
    async getURLs({commit}) {
      return commit('SET_URLS', await whitelabelService.getURLs());
    },
    async putURLs({state, dispatch}, urls) {
      try {
        state.urls = urls
        await whitelabelService.putURLs(urls)

        dispatch('addNotification',
          {message: i18n.t('notification.save.success'), type: 'success'},
          {root: true})
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    async getCSS({commit, state}) {
      const response = await whitelabelService.getCSS(state?.urls?.subdomain);
      if (response) {
        return commit('SET_CSS', response);
      } else {
        state.css.custom = exampleCSS
      }
    },
    async putCSS({dispatch}, css) {
      try {
        dispatch('addNotification',
          {message: i18n.t('notification.save.success'), type: 'success'},
          {root: true})
        await whitelabelService.putCSS(JSON.stringify(css.custom));
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    async resetCSS({commit, dispatch}) {
      try {
        dispatch('addNotification',
          {message: i18n.t('notification.reset.success'), type: 'success'},
          {root: true})
        await whitelabelService.putCSS(JSON.stringify(exampleCSS));
        commit('SET_CSS', exampleCSS);
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.reset.error'), type: 'error'},
          {root: true})
      }
    },
    async getLightLogo({commit, state}) {
      if(!state?.urls?.subdomain) {
        return commit('SET_LOGO_LIGHT', null);
      }
      const response = await whitelabelService.getLogo(state?.urls?.subdomain, WhitelabelFileTypes.LightLogo);
      if(response){
        const blob = new Blob([response], { type: 'image/png' });
        const objectURL = URL.createObjectURL(blob);
        return commit('SET_LOGO_LIGHT', objectURL);
      }
      return commit('SET_LOGO_LIGHT', null);
    },
    async getDarkLogo({commit, state}) {
      if(!state?.urls?.subdomain) {
        return commit('SET_LOGO_LIGHT', null);
      }
      const response = await whitelabelService.getLogo(state?.urls?.subdomain, WhitelabelFileTypes.DarkLogo);
      if(response){
        const blob = new Blob([response], { type: 'image/png' });
        const objectURL = URL.createObjectURL(blob);
        return commit('SET_LOGO_DARK', objectURL);
      }
      return commit('SET_LOGO_DARK', null);
    },
    async putLogo({state, dispatch}, {type, file}) {
      try {
        if (typeof state.logos !== 'object' || state.logos === null) {
          state.logos = {};
        }
        if (type === WhitelabelFileTypes.DarkLogo) {
          state.logos.dark = file
        } else {
          state.logos.light = file
        }
        dispatch('addNotification',
          {message: i18n.t('notification.save.success'), type: 'success'},
          {root: true})

        return await whitelabelService.putFile(file, type);
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    async getFavicon({commit, state}) {
      const response = await whitelabelService.getFavicon(state?.urls?.subdomain, WhitelabelFileTypes.Favicon);
      if (response?.byteLength === 0) {
        commit('SET_FAVICON', null);
        return;
      }
      const blob = new Blob([response], { type: 'image/png' });
      const objectURL = URL.createObjectURL(blob);
      commit('SET_FAVICON', objectURL);
    },
    async putFavicon({state, dispatch}, {type, file}) {
      try {
        if (typeof state.favicon !== 'object' || state.favicon === null) {
          state.favicon.custom = {};
        }
        state.favicon.custom = URL.createObjectURL(file);
        dispatch('addNotification',
          {message: i18n.t('notification.save.success'), type: 'success'},
          {root: true})
        return await whitelabelService.putFile(file, type);
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    resetFavicon({ commit }) {
      commit('SET_FAVICON', null); // Setze den Favicon-Wert im Store zurück
    },
    async getFonts({commit}) {
      return commit('SET_FONTS', await whitelabelService.getFonts());
    },
    async getFontsCSS({commit, state}) {
      const response = await whitelabelService.getFontsCSS(state?.urls?.subdomain);
      if (response) {
        commit('SET_FONTS_CSS', response);
      } else {
        return commit('SET_FONTS_CSS', '')
      }
    },
    async putFont({dispatch}, {font}) {
      try {
        const response = await whitelabelService.putFont(font);
        if(response) {
          dispatch('getFontsCSS');
          dispatch('addNotification',
            {message: i18n.t('notification.save.success'), type: 'success'},
            {root: true})
        }
      } catch (error) {
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    async resetFonts({ commit, dispatch, state }) {
      state.isLoading = true;
      try {
        const defaultFonts = [
          {
            name: defaultPrimaryFont.name,
            weight: defaultPrimaryFont.weight,
            style: defaultPrimaryFont.style,
            files: [],
            fileNames: []
          },
          {
            name: defaultSecondaryFont.name,
            weight: defaultSecondaryFont.weight,
            style: defaultSecondaryFont.style,
            files: [],
            fileNames: []
          }
        ];

        await whitelabelService.resetFonts();

        commit('SET_FONTS', defaultFonts);
        commit('SET_FONTS_CSS', '');
        dispatch('addNotification', {
          message: i18n.t('notification.reset.success'),
          type: 'success'
        }, { root: true });
      } catch (error) {
        console.error('Error resetting fonts:', error);
        dispatch('addNotification', {
          message: i18n.t('notification.reset.error'),
          type: 'error'
        }, { root: true });
      } finally {
        state.isLoading = false;
      }
    },
    async getEmailHTML({commit, state}) {
      try {
        const response = await whitelabelService.getEmailHTML(state?.urls?.subdomain);
        const fallbackHTML = exampleEmailHTML;

        if (!response || response.trim() === '') {
          console.warn('[EmailHTML] no server response – using fallback');
          commit('SET_EMAIL_HTML', fallbackHTML);
        } else {
          commit('SET_EMAIL_HTML', response);
        }
      } catch (error) {
        console.error('error when fetching email template:', error);
        commit('SET_EMAIL_HTML', exampleEmailHTML);
      }
    },
    async putEmailHTML({dispatch}, emailHtml) {
      try {
        await whitelabelService.putEmailHTML(emailHtml);
        return dispatch('addNotification',
          {message: i18n.t('notification.save.success'), type: 'success'},
          {root: true})
      } catch (error) {
        console.error('Error saving email HTML:', error);
        dispatch('addNotification',
          {message: i18n.t('notification.save.error'), type: 'error'},
          {root: true})
      }
    },
    async loadDefaultAssets({commit, dispatch}) {
      commit('SET_URLS', initialState?.urls);
      commit('SET_EMAIL_HTML', '');
      commit('SET_CSS', null);
      commit('SET_FAVICON', null);
      commit('SET_LOGO_DARK', null);
      commit('SET_LOGO_LIGHT', null);
      commit('SET_FONTS', initialState?.fonts);
      commit('SET_FONTS_CSS', '');
      dispatch('resetTheme', null, {root: true});
    },
    async getWhiteLabelAssets({ dispatch, state }, all = false) {
      try {
        const subdomain = getSubdomain();
        const isCatchableSubdomain = subdomain && subdomain !== 'localhost';

        await dispatch('getURLs');

        if (!state?.urls?.subdomain && isCatchableSubdomain) {
          if (typeof state.urls !== 'object' || state.urls === null) {
            state.urls = {};
          }
          // state.urls.subdomain = subdomain;
          // console.warn('[Fallback] setting local subdomain to:', subdomain);
        }

        if (!state?.urls?.subdomain) {
          // console.warn('no subdomain found – using default assets');
          await dispatch('loadDefaultAssets');
          return;
        }

        switch (all) {
          case true:
            await safeDispatch(dispatch, 'getFonts')
            await safeDispatch(dispatch, 'getEmailHTML')
          // fallthrough
          case false:
            await safeDispatch(dispatch, 'getCSS')
            await safeDispatch(dispatch, 'getFontsCSS')
            await safeDispatch(dispatch, 'getFavicon')
            await safeDispatch(dispatch, 'getDarkLogo')
            await safeDispatch(dispatch, 'getLightLogo')
            break;
        }
      } catch (error) {
        console.error('Error fetching assets: ', error);
      }
    },
    resetWhitelabel({commit}) {
      commit('RESET_WHITELABEL');
    },
    saveWhitelabel({commit}) {
      return commit('SAVE_WHITELABEL');
    },
    setWhitelabelValidity({state}, payload){
      state.isValid = payload;
    },
    setWhitelabelLoading({state}, payload) {
      state.isLoading = payload;
    }
  },
  getters: {
    isWhitelabelValid: state => {
      return state?.isValid
    },
    isWhitelabelLoading: state => {
      return state?.isLoading
    },
    activeWhitelabelTab: state => {
      return state.activeWhitelabelTab;
    },
    urls: state => {
      return state.urls;
    },
    email: state => {
      return state.email;
    },
    css: state => {
      return state.css;
    },
    favicon: state => {
      return state.favicon;
    },
    logos: state => {
      return state.logos;
    },
    fonts: state => {
      return state.fonts;
    }
  }
};
